import dayjs from 'dayjs/esm/index.js'

export type OrderDueOnColor = 'paid' | 'overdue' | 'dueToday' | 'default'

export const ORDER_DUE_ON_PAYMENT_TYPES = ['credit_sale', 'layaway', 'cash_on_delivery'] as const

export function allowsOrderDueOn(paymentType: string | null | undefined): boolean {
  return (ORDER_DUE_ON_PAYMENT_TYPES as readonly string[]).includes(paymentType || '')
}

export function isOrderFullyPaid(totalBalanceAmount: number | null | undefined): boolean {
  return (totalBalanceAmount ?? 0) <= 0
}

export function orderDueOnColor(
  billDueDate: string | Date | null | undefined,
  totalBalanceAmount: number | null | undefined,
  now: Date = new Date()
): OrderDueOnColor {
  if (!billDueDate) return 'default'
  if (isOrderFullyPaid(totalBalanceAmount)) return 'paid'

  const due = dayjs(billDueDate).startOf('day')
  const today = dayjs(now).startOf('day')
  if (due.isBefore(today)) return 'overdue'
  if (due.isSame(today)) return 'dueToday'
  return 'default'
}

export function orderDueOnTextClass(
  billDueDate: string | Date | null | undefined,
  totalBalanceAmount: number | null | undefined,
  now: Date = new Date()
): string {
  switch (orderDueOnColor(billDueDate, totalBalanceAmount, now)) {
    case 'paid':
      return 'text-black'
    case 'overdue':
      return 'text-red-500'
    case 'dueToday':
      return 'text-orange-500'
    default:
      return ''
  }
}

export function isOrderDueOnDisabled(paymentType: string | null | undefined): boolean {
  return !allowsOrderDueOn(paymentType)
}

export function orderDueOnCellStyle(
  billDueDate: string | Date | null | undefined,
  totalBalanceAmount: number | null | undefined,
  now: Date = new Date()
): { color?: string } {
  switch (orderDueOnColor(billDueDate, totalBalanceAmount, now)) {
    case 'paid':
      return { color: '#000000' }
    case 'overdue':
      return { color: '#ef4444' }
    case 'dueToday':
      return { color: '#f97316' }
    default:
      return {}
  }
}

export const ORDER_DUE_ON_SHORTCUT_KEYS = [
  'dueOnInOneWeek',
  'dueOnInTwoWeeks',
  'dueOnInOneMonth',
  'dueOnInTwoMonths',
  'dueOnInThreeMonths',
  'dueOnInSixMonths',
  'dueOnInOneYear'
] as const

export type OrderDueOnShortcutKey = (typeof ORDER_DUE_ON_SHORTCUT_KEYS)[number]

export function orderDueOnShortcutDates(now: Date = new Date()): {
  key: OrderDueOnShortcutKey
  date: Date
}[] {
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = now.getDate()

  const addDays = (days: number) => {
    const date = new Date(year, month, day)
    date.setDate(date.getDate() + days)
    return date
  }

  const addMonths = (months: number) => new Date(year, month + months, day)

  return [
    { key: 'dueOnInOneWeek', date: addDays(7) },
    { key: 'dueOnInTwoWeeks', date: addDays(14) },
    { key: 'dueOnInOneMonth', date: addMonths(1) },
    { key: 'dueOnInTwoMonths', date: addMonths(2) },
    { key: 'dueOnInThreeMonths', date: addMonths(3) },
    { key: 'dueOnInSixMonths', date: addMonths(6) },
    { key: 'dueOnInOneYear', date: addMonths(12) }
  ]
}
